import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// ADRB2 Arg/Arg + NR3C1 + ACE I/I — 18 months of HIIT that made everything worse.
// The aha: she was not undertrained. She was chronically overtrained in the wrong modality
// for her genetics. More effort in the same direction accelerates the damage.

export const exercisePreethi: Scenario = {
  slug: 'exercise-preethi',
  series: 'Fitness & Recovery',
  title: "She trained harder and kept gaining weight.",
  socialHook:
    "Preethi, 38, did HIIT, spinning, and bootcamp five days a week. She gained 6kg and felt worse. Her results showed the problem was not effort. It was the wrong kind of stress.",

  meta: {
    description:
      "A fictional case study: Preethi trained 5x/week for 18 months while eating in a calorie deficit, yet gained weight and developed high cortisol, insulin resistance, low ferritin, and chronic fatigue. Her ADRB2, NR3C1, and ACE results explain why high-intensity training was the wrong stress for her body.",
  },

  persona: {
    name: 'Preethi',
    age: 38,
    sex: 'Female',
    ancestry: 'British-Indian',
    occupation: 'Marketing manager',
    chiefConcern:
      "Preethi has trained five days a week for 18 months after having her second child: HIIT, spinning, and bootcamp. She tracks her food and is eating in a calorie deficit. She has still gained 6kg. She is exhausted, sleeps poorly, gets frequent colds, and has a higher resting heart rate. Her trainer says to push harder. She feels she is already at her limit.",
    familyHistory:
      "Mother: hypothyroidism, type 2 diabetes. No relevant paternal history.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent exhaustion — not relieved by rest or sleep',
      'Poor sleep quality — difficulty staying asleep, unrefreshing mornings',
      'Frequent colds and upper respiratory infections — 4–5 in the last year',
      'Weight gain of 6kg despite calorie deficit and high training volume',
    ],
    labs: [
      { name: 'Cortisol (9am)', value: '23 mcg/dL', refRange: '6–20 mcg/dL', flag: 'high' },
      { name: 'DHEA-S', value: '2.1 µmol/L', refRange: '2.7–9.2 µmol/L', flag: 'low' },
      { name: 'Ferritin', value: '14 µg/L', refRange: '20–200 µg/L', flag: 'low' },
    ],
    medications: ['No prescription medications'],
    supplements: ['No regular supplements'],
    lifestyle: [
      'Exercise: 5x/week — HIIT classes, spinning, bootcamp (18 months)',
      'Diet: tracked calorie deficit throughout',
      'Sleep: 6–7 hours, poor quality — difficulty staying asleep',
      'Non-smoker, minimal alcohol',
      'Two children; second child born approximately 2 years ago',
    ],
  },

  genetics: [
    {
      gene: 'ADRB2',
      variant: 'rs1042713 (Arg16Gly)',
      rsid: 'rs1042713',
      genotype: 'Homozygous Arg/Arg',
      plainMeaning:
        "Preethi's fat cells are becoming resistant to the signal that tells them to release stored fat — and intense exercise makes this worse, not better.",
      technicalDetail:
        "The beta-2 adrenergic receptor governs how fat cells respond to adrenaline. The Arg16 allele is associated with receptor downregulation under chronic sympathetic stimulation. Under repeated HIIT-level adrenaline exposure, Arg/Arg carriers progressively blunt the lipolytic (fat-release) signal from exercise. Combined with the high-cortisol environment her labs confirm, this drives abdominal fat storage — a feedback loop where harder training deepens the problem.",
    },
    {
      gene: 'NR3C1',
      variant: 'rs6195 (glucocorticoid receptor variant)',
      rsid: 'rs6195',
      genotype: 'Increased cortisol sensitivity',
      plainMeaning:
        "Each HIIT session gives Preethi a stress signal that lasts longer than it would for most people, helping explain her weight gain, poor sleep, and frequent illness.",
      technicalDetail:
        "NR3C1 encodes the glucocorticoid receptor. Her variant increases cellular sensitivity to cortisol. Where most people clear the cortisol spike from a HIIT session within 90 minutes, Preethi's amplified receptor response keeps the signal active far longer — driving visceral fat storage, suppressing immune function, degrading sleep, and chronically straining the adrenal axis. Her critically low DHEA-S (the adrenal reserve marker) alongside her elevated cortisol is the biochemical fingerprint of this sustained overactivation.",
    },
    {
      gene: 'ACE',
      variant: 'Insertion/deletion (rs4340)',
      rsid: 'rs4340',
      genotype: 'I/I genotype',
      plainMeaning:
        "Preethi is built for endurance, not intensity — her biology thrives on steady, longer efforts and barely adapts to the high-intensity work she's been doing.",
      technicalDetail:
        "ACE I/I is associated with superior endurance performance and oxygen efficiency, but a reduced anabolic and power adaptation to high-intensity training. The modality that produces the best hormonal and metabolic response for I/I carriers is Zone 2 cardio — sustained aerobic effort at conversational pace — where cortisol clears within 30 minutes post-session and fat oxidation continues for hours. Preethi's current program sits at almost the exact opposite end of the spectrum from what her genotype responds to.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She was doing everything harder",
      body: "Preethi did not quit. She trained through exhaustion, repeated colds, poor sleep, and the frustration of the scale moving the wrong way. She hired a trainer, tracked every meal, and kept showing up to 6am bootcamp even when she felt terrible. After 18 months, she was 6kg heavier, her resting heart rate was up, and she was getting sick every couple of months. The advice was to push harder. But she was already training more than most people and eating in a calorie deficit. Something did not add up.",
    },
    {
      type: 'narrative',
      heading: "The missing word was recovery",
      body: "Exercise is a stressor. Recovery is where the body adapts. When the stress keeps coming and recovery cannot keep up, the body starts acting as if it is under threat: cortisol rises, sleep worsens, the immune system weakens, and resting heart rate climbs. Preethi's labs fit that pattern: high morning cortisol, low DHEA-S, and low ferritin. This is not a willpower problem. It is a recovery problem, and more of the same training keeps feeding it.",
    },
    {
      type: 'reveal',
      heading: "Her workouts were sending the wrong signal",
      body: "The same class can send different signals in different bodies. Preethi's ADRB2 result means repeated adrenaline bursts from HIIT can make fat cells less responsive to the signal that releases stored fat. Her NR3C1 result means the cortisol signal from each hard session is stronger and lasts longer. Together, the pattern is blunt fat release, raise stress, and store more. Working harder does not break that cycle. It deepens it.",
    },
    {
      type: 'reveal',
      heading: "Her body was built for steady work",
      body: "Preethi's ACE result points toward endurance: steady aerobic work, efficient oxygen use, and longer efforts at a conversational pace. Her current program is the opposite: repeated high-intensity spikes. Low ferritin adds another burden. At 14 µg/L, low iron can make the cells' energy systems work poorly, so every workout feels harder than it should and creates a bigger stress response. The answer is not less discipline. It is a different training target.",
    },
    {
      type: 'action',
      heading: "What changes when recovery is the goal",
      items: [
        "Cut training to 3 sessions per week and pause HIIT. Replace it with Zone 2 cardio: walking, cycling, or swimming at a pace where full sentences are comfortable for 45–60 minutes. For the first 8 weeks, the goal is recovery.",
        "Add one controlled resistance session per week. Strength work can improve insulin sensitivity without the same cortisol spike as bootcamp. Keep it moderate during the recovery phase.",
        "Address ferritin with her clinician. Low iron can make each workout feel harder and raise the stress cost of training. Recheck ferritin in about 6 weeks and aim above 50 µg/L before increasing volume.",
        "Use resting heart rate as a daily recovery gauge. Measure it on waking. When it returns to her normal range and stays stable for two weeks, training can increase gradually.",
        "Wait 8 weeks before judging body composition. Cortisol-driven weight gain can improve once the stress load drops, but the body needs time before the scale tells the full story.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "What her training was doing to her metabolic picture",
      caption:
        "Preethi was not undertrained. She was overtrained in the wrong style of exercise for her biology. The plan redirects effort toward the kind of work her body can recover from.",
      baselineLabel: "5x/week HIIT — cortisol 628, HOMA-IR 4.8, resting HR 84",
      baseline: 82,
      levers: [
        {
          label: "Switch to Zone 2 cardio 3x/week",
          delta: -24,
          description: "Removes the chronic cortisol stimulus entirely. Zone 2 cortisol clears within 30 minutes post-session. The single biggest lever — addresses the root cause of HPA axis overload.",
        },
        {
          label: "8-week deload period",
          delta: -14,
          description: "Allows HPA axis recovery — DHEA-S to rebuild, cortisol to normalize, ADRB2 receptor sensitivity to restore. The recovery curve requires the full duration; partial deloads do not achieve the same reset.",
        },
        {
          label: "Add resistance training 1x/week",
          delta: -12,
          description: "Improves insulin sensitivity in muscle tissue without the cortisol spike of HIIT. Addresses the insulin resistance reading directly. One session per week is the correct dose during the recovery phase.",
        },
        {
          label: "Correct ferritin to >50 µg/L",
          delta: -10,
          description: "Removes the mitochondrial impairment that is amplifying the stress response to every session. Low ferritin makes perceived exertion artificially high — correcting it changes how hard the same workout actually feels.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'exercise-preethi',
    label: 'Explore your exercise genetics in Coach',
  },
}
